import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import {
  Server, Globe, HardDrive, RefreshCw, Loader2, Database,
  CheckCircle, AlertTriangle, FileText, Settings, Shield,
  ChevronDown, ChevronUp, Wifi, Lock, Search, Network, Layers
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function OdooInstanceCard({ instance, serverIp }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-white/10 rounded-lg overflow-hidden bg-white/[0.02] hover:bg-white/[0.04] transition-all"
      data-testid={`instance-card-${instance.id}`}>
      <button onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left">
        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
          <Globe className="w-5 h-5 text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-white truncate">
              {instance.subdomain || instance.service_name || "Unknown Instance"}
            </p>
            {instance.ssl && (
              <Badge className="text-[9px] bg-green-500/10 text-green-400 border-green-500/20 px-1.5">
                <Lock className="w-2.5 h-2.5 mr-0.5" /> SSL
              </Badge>
            )}
          </div>
          <p className="text-xs text-zinc-500 mt-0.5">
            Port {instance.http_port} | DB: {instance.db_name || "N/A"}
          </p>
        </div>
        <Badge className="text-[10px] bg-blue-500/10 text-blue-400 border-blue-500/20">
          {instance.service_name}
        </Badge>
        {expanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-2 border-t border-white/5 pt-3">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <InfoRow icon={Globe} label="Subdomain" value={instance.subdomain || "Not configured"} />
            <InfoRow icon={Network} label="HTTP Port" value={instance.http_port} />
            <InfoRow icon={Wifi} label="Longpoll Port" value={instance.longpolling_port} />
            <InfoRow icon={Database} label="Database" value={instance.db_name || "N/A"} />
            <InfoRow icon={Settings} label="Config" value={instance.config_path || "N/A"} full />
            <InfoRow icon={FileText} label="Log Path" value={instance.log_path || "N/A"} full />
            <InfoRow icon={Server} label="Service" value={instance.service_name || "N/A"} />
            <InfoRow icon={Layers} label="Nginx Config" value={instance.nginx_config || "N/A"} />
          </div>
          {instance.description && (
            <p className="text-[10px] text-zinc-500 mt-1 italic">{instance.description}</p>
          )}
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, full }) {
  return (
    <div className={`flex items-start gap-2 ${full ? "col-span-2" : ""}`}>
      <Icon className="w-3.5 h-3.5 text-zinc-500 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <span className="text-zinc-500">{label}: </span>
        <span className="text-zinc-300 font-mono break-all">{value}</span>
      </div>
    </div>
  );
}

function NginxConfigCard({ config }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-white/[0.02] border border-white/10 rounded-lg"
      data-testid={`nginx-config-${config.filename}`}>
      <div className="w-8 h-8 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center shrink-0">
        <FileText className="w-4 h-4 text-orange-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white font-mono">{config.filename}</p>
        <p className="text-xs text-zinc-500 mt-0.5">
          {config.server_names?.join(", ") || "No server names"} {config.upstream_port ? `-> port ${config.upstream_port}` : ""}
        </p>
      </div>
      {config.ssl && (
        <Badge className="text-[9px] bg-green-500/10 text-green-400 border-green-500/20 px-1.5">
          <Lock className="w-2.5 h-2.5 mr-0.5" /> SSL
        </Badge>
      )}
    </div>
  );
}

export default function Infrastructure() {
  const { token } = useAuth();
  const [servers, setServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState("");
  const [infrastructure, setInfrastructure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchServers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/servers`, { headers });
      setServers(data);
      if (data.length > 0 && !selectedServer) {
        setSelectedServer(data[0].id);
      }
    } catch (err) {
      toast.error("Failed to load servers");
    }
  }, [token]);

  const fetchInfrastructure = useCallback(async () => {
    if (!selectedServer) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/infrastructure/${selectedServer}`, { headers });
      setInfrastructure(data);
    } catch (err) {
      toast.error("Failed to load infrastructure");
    } finally {
      setLoading(false);
    }
  }, [selectedServer, token]);

  useEffect(() => { fetchServers(); }, [fetchServers]);
  useEffect(() => { fetchInfrastructure(); }, [fetchInfrastructure]);

  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      const { data } = await axios.post(`${API}/infrastructure/${selectedServer}/discover`, {}, { headers });
      setInfrastructure(data);
      toast.success(`Discovered ${data.odoo_instances?.length || 0} Odoo instances`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Discovery failed");
    } finally {
      setDiscovering(false);
    }
  };

  const selectedSrv = servers.find(s => s.id === selectedServer);
  const instances = infrastructure?.odoo_instances || [];
  const nginxConfigs = infrastructure?.nginx_configs || [];
  const serverInfo = infrastructure?.server_info || {};

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="infrastructure-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white tracking-tight">Infrastructure</h1>
        <p className="text-sm text-zinc-500 mt-1">Server services, Odoo instances, Nginx configs, and system knowledge</p>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={selectedServer} onValueChange={setSelectedServer}>
          <SelectTrigger className="w-72 bg-white/5 border-white/10 text-white" data-testid="server-select">
            <SelectValue placeholder="Select a server" />
          </SelectTrigger>
          <SelectContent>
            {servers.map(s => (
              <SelectItem key={s.id} value={s.id} data-testid={`server-option-${s.id}`}>
                <span className="flex items-center gap-2">
                  <Server className="w-4 h-4" />
                  {s.name} ({s.ip_address})
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button onClick={handleDiscover} disabled={!selectedServer || discovering}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="discover-btn">
          {discovering ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
          {discovering ? "Discovering..." : "Auto-Discover"}
        </Button>

        <Button variant="outline" onClick={fetchInfrastructure}
          className="border-white/10 text-zinc-400 hover:text-white"
          data-testid="refresh-infra-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Server Info + Nginx */}
          <div className="space-y-6">
            {/* Server Info */}
            <Card className="bg-white/[0.02] border-white/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-zinc-400 flex items-center gap-2">
                  <Server className="w-4 h-4" /> Server Info
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-xs">
                {selectedSrv && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Name</span>
                      <span className="text-white font-medium">{selectedSrv.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">IP</span>
                      <span className="text-white font-mono">{selectedSrv.ip_address}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-500">OS</span>
                      <span className="text-white">{selectedSrv.os_type}</span>
                    </div>
                  </>
                )}
                {serverInfo.hostname && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Hostname</span>
                    <span className="text-white font-mono">{serverInfo.hostname}</span>
                  </div>
                )}
                {serverInfo.firewall && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Firewall</span>
                    <span className="text-emerald-400">{serverInfo.firewall}</span>
                  </div>
                )}
                {infrastructure?.services?.length > 0 && (
                  <div className="pt-2 border-t border-white/5">
                    <span className="text-zinc-500 block mb-1">Services:</span>
                    <div className="flex flex-wrap gap-1">
                      {infrastructure.services.map(svc => (
                        <Badge key={svc} className="text-[9px] bg-zinc-800 text-zinc-400 border-zinc-700">
                          {svc}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {!infrastructure?.odoo_instances?.length && !infrastructure?.discovered_at && (
                  <div className="text-center py-4">
                    <Search className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                    <p className="text-zinc-500 text-xs">No infrastructure data yet</p>
                    <p className="text-zinc-600 text-[10px]">Click "Auto-Discover" to scan this server</p>
                  </div>
                )}
                {infrastructure?.discovered_at && (
                  <div className="pt-2 border-t border-white/5 text-[10px] text-zinc-600">
                    Last discovered: {new Date(infrastructure.discovered_at).toLocaleString()}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Nginx Configs */}
            {nginxConfigs.length > 0 && (
              <Card className="bg-white/[0.02] border-white/10">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-zinc-400 flex items-center gap-2">
                    <Shield className="w-4 h-4" /> Nginx Configs ({nginxConfigs.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {nginxConfigs.map((nc, i) => (
                    <NginxConfigCard key={i} config={nc} />
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column: Odoo Instances */}
          <div className="lg:col-span-2">
            <Card className="bg-white/[0.02] border-white/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-zinc-400 flex items-center gap-2">
                  <Layers className="w-4 h-4" /> Odoo Instances ({instances.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {instances.length === 0 ? (
                  <div className="text-center py-12">
                    <Globe className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500 text-sm">No Odoo instances discovered</p>
                    <p className="text-zinc-600 text-xs mt-1">
                      Select a server and click "Auto-Discover" to detect running Odoo instances
                    </p>
                  </div>
                ) : (
                  instances.map(inst => (
                    <OdooInstanceCard key={inst.id} instance={inst} serverIp={selectedSrv?.ip_address} />
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
